import _ from 'lodash';
import { useState, useEffect, forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { randomBetween, shuffle } from '../../utils/utils';
import TemplatePreview from '../webapp/template-editor/TemplatePreview';
import { loadFonts } from '../../utils/fonts';
import './PrintDemo.styl';
import { getProposalTemplates } from '../../selectors/selectors';

const PrintDemo = forwardRef((props, previewContainer) => {
  const { backdrop, greenScreenOverlayIds } = props;

  /**
   * Load green screen overlays.
   */
  const greenScreenOverlays = useSelector((state) => {
    return _.compact(
      (greenScreenOverlayIds ?? []).map((overlayId) => state.proposalOverlays.data[overlayId]?.imageUrl)
    );
  });

  /**
   * Array of print templates from the store
   */
  const printTemplates = useSelector(getProposalTemplates, _.isEqual);

  /**
   * Whether more than one template exists in the store
   * @type {boolean}
   */
  const hasMultipleTemplates = printTemplates.length > 1;

  // Select template fonts from store
  const fonts = useSelector((state) => [
    ...(state.proposalData.data.printTemplateFonts ?? []),
    ...(state.proposalData.data.overlaysFonts ?? []),
  ]);

  // State for lightbox/carousel
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // State for font loading
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // State for tracking container sizes
  const [stageSize, setStageSize] = useState(0);
  const [screenSize, setScreenSize] = useState(0);

  // Load font dependencies for active printTemplates
  useEffect(() => {
    const controller = new AbortController();
    if (!printTemplates.length) {
      return;
    }
    const loadTemplateFonts = async () => {
      try {
        await loadFonts(fonts, controller);
        setFontsLoaded(true);
      } catch (err) {
        //
      }
    };
    loadTemplateFonts();

    // Cleanup function to abort async requests
    return () => {
      controller.abort();
      setFontsLoaded(false);
    };
  }, [printTemplates]);

  // Set stage and screen size based on measurements from DOM
  const getStageSize = _.debounce(() => {
    if (previewContainer.current) {
      const containerWidth = previewContainer.current.clientWidth;
      const containerHeight = previewContainer.current.clientHeight;

      const isMobile = window.innerWidth < 768;

      // On mobile (stacked layout), use container width since height may be 0
      const mobileStageSize = containerWidth;
      // On desktop (side-by-side), use the smaller of width/height as before
      const sideBySideStageSize = Math.min(containerWidth, containerHeight || containerWidth);

      const stageSize = isMobile ? mobileStageSize : sideBySideStageSize;

      setStageSize(stageSize);
    }
    setScreenSize(Math.min(window.innerWidth, window.innerHeight));
  }, 100);

  // Set up getStageSize listeners on window resize
  useEffect(() => {
    window.addEventListener('resize', getStageSize, false);
    getStageSize();
    return () => {
      window.removeEventListener('resize', getStageSize, false);
    };
  }, []);

  // Call getStageSize once when previewContainer mounts
  useEffect(() => {
    getStageSize();
  }, [previewContainer.current]);

  /**
   * Generate template with filler image layers.
   * @param {object} template - Template data
   * @returns {object[]} - Complete template data
   */
  const getTemplateWithUpdatedImages = (oldTemplate) => {
    const template = _.cloneDeep(oldTemplate);

    // Give buffer space for placeholders in terms of z-index values.
    const bufferSize = 1 + greenScreenOverlays.length;
    template.placeholders.forEach((placeholder) => {
      const baseZIndex = placeholder.zIndex;

      [].concat(template.placeholders ?? [], template.images ?? [], template.texts ?? []).forEach((layer) => {
        if (layer === template) {
          return;
        }

        if (layer.zIndex > baseZIndex) {
          layer.zIndex += bufferSize;
        }
      });
    });

    // Generate series of preview shots for a random model
    const len = template.placeholders.length;
    const model = randomBetween(4, 6);
    const modelImgCount = 4;
    const modelImgs = shuffle([...Array(modelImgCount).keys()]).slice(0, len);
    template.images = [
      ...template.images, // Images from template (background, overlay, etc.)
      ...modelImgs.reduce(
        (allImgs, img, i) => [
          ...allImgs,
          {
            ...template.placeholders[i],
            src: backdrop,
            resizeMode: 'cover',
          },
          {
            ...template.placeholders[i],
            src: `/static/model/${model}/${img}.png`,
            zIndex: template.placeholders[i].zIndex + 1,
            resizeMode: 'fit',
          },
          ...greenScreenOverlays.map((imageUrl, index) => ({
            ...template.placeholders[i],
            src: imageUrl,
            zIndex: template.placeholders[i].zIndex + 2 + index,
            resizeMode: 'fit',
          })),
        ],
        []
      ),
    ];

    // No need for placeholders anymore. They are replaced with new image layers.
    template.placeholders = [];

    return template;
  };

  /**
   * Array of templates with filler images applied
   */
  const printTemplatesWithImages = useMemo(() => {
    return printTemplates.map((template) => getTemplateWithUpdatedImages(template));
  }, [printTemplates]);

  /**
   * Go to previous carousel slide
   */
  const goToPrevious = (event) => {
    event.stopPropagation(); // Avoid closing lightbox
    setCarouselIndex((current) => {
      if (current === 0) {
        return printTemplatesWithImages.length - 1;
      }
      return current - 1;
    });
  };

  /**
   * Go to next carousel slide
   */
  const goToNext = (event) => {
    event.stopPropagation(); // Avoid closing lightbox
    setCarouselIndex((current) => {
      if (current === printTemplatesWithImages.length - 1) {
        return 0;
      }
      return current + 1;
    });
  };

  const handleLightboxToggleClick = (event) => {
    if (!isLightboxOpen) {
      setIsLightboxOpen(true);
    }
    event.stopPropagation(); // Avoid closing lightbox
  };

  /**
   * Render the carousel of templates
   * @param {boolean} isInLightbox - Whether the carousel is rendering inside a lightbox
   * @returns {React.JSX.Element}
   */
  const renderCarousel = (isInLightbox) => {
    const maxSize = isInLightbox ? screenSize - 100 : stageSize - 80;
    const activeTemplate = printTemplatesWithImages[carouselIndex];
    if (!activeTemplate) {
      return null;
    }
    const ratio = activeTemplate.height / activeTemplate.width;
    const renderHeight = ratio > 1 ? maxSize : maxSize * ratio;
    const renderWidth = ratio > 1 ? maxSize / ratio : maxSize;
    return (
      <div className={['carousel', isInLightbox && 'carousel--lightbox'].filter(Boolean).join(' ')}>
        <div className="carousel__frame">
          {hasMultipleTemplates && (
            <button onClick={goToPrevious} className="carousel__arrow carousel__arrow--prev" data-testid="carouselPrev">
              View previous template
            </button>
          )}
          <TemplatePreview key={activeTemplate.id} stageWidth={maxSize} stageHeight={maxSize} config={activeTemplate} />
          {hasMultipleTemplates && (
            <button onClick={goToNext} className="carousel__arrow carousel__arrow--next" data-testid="carouselNext">
              View next template
            </button>
          )}
          <button
            data-testid="lightboxToggle"
            className="carousel__lightbox-toggle"
            onClick={handleLightboxToggleClick}
            style={{ width: renderWidth, height: renderHeight }}
          >
            Toggle lightbox
          </button>
        </div>
        {!isInLightbox && hasMultipleTemplates && (
          <nav className="carousel__dots dots">
            {[...Array(printTemplatesWithImages.length).keys()].map((index) => (
              <button
                key={index}
                data-testid={`carouselDot${index}`}
                className={['dots__dot', carouselIndex === index && 'dots__dot--active'].filter(Boolean).join(' ')}
                onClick={() => setCarouselIndex(index)}
              >
                Go to template {index}
              </button>
            ))}
          </nav>
        )}
      </div>
    );
  };

  // Return null if not ready to render
  if (!stageSize || !fontsLoaded) {
    return null;
  }

  return (
    <div className="proposal-carousel">
      {renderCarousel(false)}
      {!!isLightboxOpen && (
        <div className="lightbox" onClick={() => setIsLightboxOpen(false)}>
          <div className="lightbox__bg" data-testid="templateLightboxBg" />
          {renderCarousel(true)}
          <button
            className="lightbox__close"
            onClick={() => setIsLightboxOpen(false)}
            data-testid="templateLightboxClose"
          />
        </div>
      )}
    </div>
  );
});

export default PrintDemo;
